import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gameRoomService from '../../services/gameRoomService';

interface QuickAction {
  icon: string;
  label: string;
  action: () => void;
  primary?: boolean;
}

const QuickActions: React.FC = () => {
  const navigate = useNavigate();
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [roomId, setRoomId] = useState('');
  const [joining, setJoining] = useState(false);

  const handleJoinByID = async () => {
    if (!roomId.trim()) {
      alert('Please enter a Room ID');
      return;
    }

    try {
      setJoining(true);
      const response = await gameRoomService.joinRoom(roomId.trim());
      if (response.success) {
        window.location.href = `/game/${roomId.trim()}`;
      } else {
        alert(response.message || 'Failed to join room');
      }
    } catch (error: any) {
      console.error('Error joining room:', error);
      alert(error?.response?.data?.message || 'Failed to join room. Please check the Room ID.');
    } finally {
      setJoining(false);
      setShowJoinModal(false);
      setRoomId('');
    }
  };

  const actions: QuickAction[] = [
    {
      icon: '➕',
      label: 'Create Room',
      action: () => navigate('/rooms/create'),
      primary: true
    },
    {
      icon: '🔑',
      label: 'Join by ID',
      action: () => setShowJoinModal(true),
      primary: false
    },
    {
      icon: '⚡',
      label: 'Quick Play',
      action: () => navigate('/rooms'),
      primary: false
    },
    {
      icon: '👥',
      label: 'Invite Friends',
      action: () => {
        if (navigator.share) {
          navigator.share({
            title: 'Join me in Card Game Platform',
            text: 'Lets play Kadi Ni Tidi together!',
            url: window.location.origin
          });
        }
      },
      primary: false
    }
  ];

  return (
    <>
      <div className="quick-actions">
        <h2 className="section-title">Quick Actions</h2>
        <div className="quick-actions-buttons">
          {actions.map((action, index) => (
            <button
              key={index}
              className={`quick-action-btn glass-card ${action.primary ? 'primary' : ''}`}
              onClick={action.action}
            >
              <span className="btn-icon">{action.icon}</span>
              <span className="btn-label">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Join by ID Modal */}
      {showJoinModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowJoinModal(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: '#fff', marginBottom: '1rem', fontSize: '1.25rem' }}>
              🔑 Join Room by ID
            </h3>
            <p style={{ color: '#94a3b8', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Enter the Room ID you want to join:
            </p>
            <input
              type="text"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              placeholder="Enter Room ID (e.g., 67a1b2c3d4e5f6g7h8i9j0k1)"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleJoinByID();
                }
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                fontSize: '0.95rem',
                marginBottom: '1.5rem',
                outline: 'none'
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => {
                  setShowJoinModal(false);
                  setRoomId('');
                }}
                disabled={joining}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: '#94a3b8',
                  fontSize: '0.95rem',
                  cursor: joining ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleJoinByID}
                disabled={joining || !roomId.trim()}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: joining || !roomId.trim() 
                    ? 'rgba(99, 102, 241, 0.5)' 
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontSize: '0.95rem',
                  cursor: joining || !roomId.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  boxShadow: joining || !roomId.trim() ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)'
                }}
              >
                {joining ? 'Joining...' : 'Join Room'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickActions;
